import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import TablePager from "@/components/TablePager";
import SendInvoiceModal from "@/components/SendInvoiceModal";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import {
  Receipt, FileDown, Search, Pencil, Layers, Split, Send, AlertCircle,
  ChevronRight, ChevronDown, Download, Trash2, X,
} from "lucide-react";

const PAGE_SIZE = 25;

// Group a (merged) invoice's line items by their source job so we can show the
// individual jobs that were consolidated, each with its own line items + subtotal.
function groupLinesByJob(inv) {
  const groups = {};
  (inv.line_items || []).forEach((l) => {
    const key = l.job_id || l.job_ref || "(custom)";
    if (!groups[key]) groups[key] = { job_ref: l.job_ref || "(custom)", job_id: l.job_id, lines: [], subtotal: 0 };
    groups[key].lines.push(l);
    groups[key].subtotal += Number(l.qty || 0) * Number(l.rate || 0);
  });
  return Object.values(groups);
}

const TOTAL_COLS = 11; // checkbox, Invoice #, Customer, Client Ref, Jobs, Invoice Date, Due Date, Amount, Status, RCTI, Actions

// Invoicing tab. Every billable job gets a single invoice (auto-materialised by
// the backend). Admin can edit each line item (reflects into Job Status/History
// via the live invoice join), MERGE several of a customer's invoices into one
// consolidated invoice, edit that, and email it to the client via Resend.
// `cl` = the readable colour to use in light mode (the dark-mode pastels are too
// light on a white field).
// Draft -> Pending Payment (automatic, the moment "Send" is clicked) ->
// Overdue (automatic, once the due date passes) -> Paid (manual, terminal).
// Pending/Overdue are shown here so admin can still see or override them, but
// they're normally set automatically by the backend on send / on every
// Invoicing tab load.
const INVOICE_STATUS_OPTIONS = [
  { value: "draft",       label: "Draft",              color: "#a5b4fc", cl: "#4f46e5" },
  { value: "pending",     label: "Pending Payment",    color: "#facc15", cl: "#b45309" },
  { value: "overdue",     label: "Overdue",            color: "#ef4444", cl: "#dc2626" },
  { value: "paid",        label: "Paid",               color: "#22c55e", cl: "#15803d" },
];
const RCTI_STATUS_OPTIONS = [
  { value: "not_sent", label: "RCTI Not Sent", color: "#9ca3af", cl: "#6b7280" },
  { value: "sent",     label: "RCTI Sent",     color: "#86efac", cl: "#16a34a" },
  { value: "paid",     label: "RCTI Paid",     color: "#22c55e", cl: "#15803d" },
  { value: "pending",  label: "RCTI Pending",  color: "#facc15", cl: "#b45309" },
  { value: "overdue",  label: "RCTI Overdue",  color: "#ef4444", cl: "#dc2626" },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusDropdown({ options, value, onChange, disabled, testId }) {
  const { theme } = useTheme();
  const meta = options.find((o) => o.value === value) || options[0];
  const c = theme === "light" ? (meta.cl || meta.color) : meta.color;
  return (
    <select
      value={value || options[0].value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="text-xs font-semibold rounded-md px-2 py-1.5 border focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-50"
      style={{ background: c + "1f", color: c, borderColor: c + "55" }}
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#0b1430] text-white">{o.label}</option>
      ))}
    </select>
  );
}

export default function Invoicing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const location = useLocation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("normal");      // "normal" | "merged"
  const [filter, setFilter] = useState(() => new URLSearchParams(location.search).get("inv") || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState({});  // invoice id → bool (bulk selection, scoped to what's currently visible)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [expanded, setExpanded] = useState({});  // merged invoice id → show constituent jobs
  const [sending, setSending] = useState(null);  // invoice being sent

  const editInvoice = (inv) => navigate(`/dashboard/invoicing/${inv.id}/edit`);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Normal = standalone singles (not absorbed into a merge). Merged = consolidated.
  const normal = useMemo(() => invoices.filter((i) => i.type === "single" && !i.merged_into), [invoices]);
  const merged = useMemo(() => invoices.filter((i) => i.type === "merged"), [invoices]);
  const source = tab === "merged" ? merged : normal;
  const normalOverdue = useMemo(() => normal.filter((i) => i.invoice_status === "overdue").length, [normal]);
  const mergedOverdue = useMemo(() => merged.filter((i) => i.invoice_status === "overdue").length, [merged]);
  const totalOverdue = normalOverdue + mergedOverdue;
  const searching = filter.trim().length > 0 || !!dateFrom || !!dateTo;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A Client Ref (or invoice #/carrier ref) search, or an invoice-date range,
    // must surface a match whichever bucket it lives in, regardless of which
    // tab is active — so once either filter is set, matching runs over every
    // invoice, not just the active tab's subset.
    const base = q || dateFrom || dateTo ? invoices : source;
    return base.filter((i) => {
      if (q) {
        const hit = [i.invoice_number, i.customer_name, ...(i.job_refs || []), ...(i.client_job_refs || [])]
          .filter(Boolean).map((x) => String(x).toLowerCase()).some((x) => x.includes(q));
        if (!hit) return false;
      }
      const invDate = (i.invoice_date || "").slice(0, 10);
      if (dateFrom && invDate < dateFrom) return false;
      if (dateTo && invDate > dateTo) return false;
      return true;
    });
  }, [invoices, source, filter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
  useEffect(() => { setPage(1); }, [filter, dateFrom, dateTo, tab]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  // Clear the bulk selection whenever the visible list changes, so an invoice
  // that's been filtered/tabbed out of view can never be silently included in
  // a bulk delete (mirrors the same safeguard on the Job History page).
  useEffect(() => { setSelected({}); }, [filter, dateFrom, dateTo, tab]);

  // Bulk selection — scoped to whatever's currently visible (`filtered`), so it
  // stays correct whether the active tab or a cross-tab search produced the rows.
  // Merging further requires the customer-match check below, and is only ever
  // offered on the Normal tab (see the bulk action bar).
  const selectedList = useMemo(() => filtered.filter((i) => selected[i.id]), [filtered, selected]);
  const selCustomers = useMemo(() => new Set(selectedList.map((i) => i.client_user_id || "_none")), [selectedList]);
  const selTotal = selectedList.reduce((a, i) => a + Number(i.total || 0), 0);
  const sameCustomer = selCustomers.size <= 1;
  const allOnPageSelected = pageItems.length > 0 && pageItems.every((i) => selected[i.id]);
  const toggleSelectAllOnPage = () => {
    const checked = !allOnPageSelected;
    setSelected((s) => {
      const next = { ...s };
      pageItems.forEach((i) => { next[i.id] = checked; });
      return next;
    });
  };

  const setStatus = async (inv, field, val) => {
    setBusyId(inv.id);
    // optimistic
    setInvoices((list) => list.map((x) => (x.id === inv.id ? { ...x, [field]: val } : x)));
    try {
      await api.patch(`/invoices/${inv.id}`, { [field]: val });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    } finally { setBusyId(null); }
  };

  const openPdf = async (inv) => {
    // Open the tab synchronously, inside the click handler — opening it only
    // after the await below breaks the user-gesture chain and mobile browsers
    // (iOS Safari in particular) silently strand it on about:blank instead of
    // navigating to the blob once it's ready.
    //
    // Deliberately NOT passing "noopener"/"noreferrer" here: those make
    // window.open() return null (per spec, since the caller gets no handle to
    // a window it isn't allowed to be the opener of), which is exactly what
    // this code needs below to route the PDF into the tab once it's fetched.
    // The tab still opens either way — without a reference it just gets
    // orphaned on about:blank forever, which is the "blank PDF page" bug.
    // Safe here because we immediately point it at our own same-origin blob
    // URL, never at third-party or user-controlled content.
    const w = window.open("", "_blank");
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (!w) { toast.error("Please allow pop-ups to preview invoices"); return; }
      w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      if (w) w.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doMerge = async () => {
    if (selectedList.length < 2) { toast.error("Select at least two invoices to merge"); return; }
    if (!sameCustomer) { toast.error("All selected invoices must belong to the same customer"); return; }
    if (!window.confirm(`Merge ${selectedList.length} jobs for ${selectedList[0]?.customer_name} (${fmt(selTotal)}) into one consolidated invoice you can edit and send?`)) return;
    try {
      const { data } = await api.post("/invoices/merge", { invoice_ids: selectedList.map((i) => i.id) });
      toast.success(`Merged into ${data.invoice_number}`);
      setSelected({});
      navigate(`/dashboard/invoicing/${data.id}/edit`);  // open the editor page on the new merged invoice
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doUnmerge = async (inv) => {
    if (!window.confirm(`Unmerge ${inv.invoice_number}? Its ${inv.child_invoice_ids?.length || 0} original invoices are restored.`)) return;
    try {
      await api.post(`/invoices/${inv.id}/unmerge`);
      toast.success("Invoice unmerged — originals restored");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Bulk download — available to any signed-in role that can see this page.
  // Reuses the same single-invoice fetch+save as the per-row Download button,
  // just looped with a short stagger so the browser doesn't treat a burst of
  // simultaneous downloads as a popup flood and block them.
  const bulkDownloadSelected = async () => {
    if (selectedList.length === 0) return;
    setBulkBusy(true);
    try {
      for (const inv of selectedList) {
        await downloadPdf(inv);
        await new Promise((r) => setTimeout(r, 300));
      }
      toast.success(`Downloaded ${selectedList.length} invoice${selectedList.length !== 1 ? "s" : ""}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Bulk delete — admin only (also enforced server-side: DELETE /invoices/{id}
  // requires the admin role). Mirrors Job History's deleteSelected: fire every
  // delete, tolerate partial failure, report what actually happened.
  const bulkDeleteSelected = async () => {
    if (!isAdmin || selectedList.length === 0) return;
    setBulkBusy(true);
    try {
      const ids = selectedList.map((i) => i.id);
      const results = await Promise.allSettled(ids.map((id) => api.delete(`/invoices/${id}`)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) toast.success(`Deleted ${ids.length} invoice${ids.length !== 1 ? "s" : ""}`);
      else toast.error(`Deleted ${ids.length - failed} of ${ids.length}; ${failed} failed`);
      setSelected({});
      setConfirmDeleteOpen(false);
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard/job-status" replace />;

  return (
    <div data-testid="invoicing-page">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="brand-title text-2xl sm:text-3xl flex items-center gap-3">
            <Receipt className="text-pink-400" size={26} /> Invoicing
            {totalOverdue > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500/15 text-red-400" data-testid="overdue-badge">
                <AlertCircle size={13} /> {totalOverdue} Overdue
              </span>
            )}
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Edit line items, merge a client's jobs into one invoice &amp; email it via Resend · edits reflect in Job Status / History
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search invoice # / customer / client ref" className="df-input-rect" style={{ paddingLeft: "2.5rem" }} data-testid="invoice-search" />
          </div>
          <span className="text-white/80 font-semibold text-sm">From:</span>
          <DatePicker value={dateFrom} onChange={setDateFrom} max={dateTo || undefined} className="!py-2 !text-sm" data-testid="invoice-date-from" />
          <span className="text-white/80 font-semibold text-sm">To:</span>
          <DatePicker value={dateTo} onChange={setDateTo} min={dateFrom || undefined} className="!py-2 !text-sm" data-testid="invoice-date-to" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-white/60 hover:text-white text-sm underline">Clear</button>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { key: "normal", label: `Normal (${normal.length})`, icon: Receipt, overdue: normalOverdue },
          { key: "merged", label: `Merged (${merged.length})`, icon: Layers, overdue: mergedOverdue },
        ].map((t) => {
          const on = tab === t.key && !searching;
          const Icon = t.icon;
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                on ? "bg-pink-400/20 text-pink-300" : "bg-white/5 text-white/50 hover:bg-white/10"}`}
              data-testid={`invoice-tab-${t.key}`}>
              <Icon size={14} /> {t.label}
              {t.overdue > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white" data-testid={`overdue-badge-${t.key}`}>
                  {t.overdue}
                </span>
              )}
            </button>
          );
        })}
        {searching && (
          <span className="text-xs text-white/40">
            Showing matches from Normal + Merged
            {filter.trim() ? ` for "${filter.trim()}"` : ""}
            {(dateFrom || dateTo) ? ` · ${dateFrom || "…"} to ${dateTo || "…"}` : ""}
          </span>
        )}
      </div>

      {/* Bulk action bar — appears once one or more invoices are selected.
          Download is available to every role that reaches this page; Delete
          is strictly admin-only (double-gated: hidden here AND rejected by
          the backend for any other role); Merge keeps its original normal-tab,
          single-customer, 2+ requirement. */}
      {selectedList.length > 0 && (
        <div className="rounded-xl border border-pink-400/30 bg-pink-400/10 px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-3" data-testid="bulk-actions-bar">
          <div className="text-sm text-white/80">
            <span className="font-bold">{selectedList.length}</span> invoice{selectedList.length !== 1 ? "s" : ""} selected · <span className="font-mono font-bold">{fmt(selTotal)}</span>
            {tab === "normal" && (
              sameCustomer
                ? <span className="ml-3 text-white/50 text-xs">for <span className="text-white/80">{selectedList[0]?.customer_name}</span> → one consolidated invoice</span>
                : <span className="ml-3 text-amber-300 text-xs inline-flex items-center gap-1">
                    <AlertCircle size={13} /> Merging is single-client only — your selection spans multiple customers.
                  </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected({})} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-xs font-semibold">Clear</button>
            <button onClick={bulkDownloadSelected} disabled={bulkBusy}
              className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              style={{ background: "rgba(25,230,200,0.15)", color: "#19e6c8" }} data-testid="bulk-download-btn">
              <Download size={15} /> {bulkBusy ? "Working…" : "Download"}
            </button>
            {isAdmin && (
              <button onClick={() => setConfirmDeleteOpen(true)} disabled={bulkBusy}
                className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }} data-testid="bulk-delete-btn">
                <Trash2 size={15} /> Delete
              </button>
            )}
            {tab === "normal" && (
              <button onClick={doMerge} disabled={selectedList.length < 2 || !sameCustomer || bulkBusy}
                className="px-4 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40"
                style={{ background: "linear-gradient(90deg,#ec4899,#db2777)" }} data-testid="merge-btn">
                <Layers size={15} /> Merge jobs into one invoice
              </button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-white/60">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleSelectAllOnPage}
                    className="accent-pink-500 w-4 h-4 cursor-pointer"
                    aria-label="Select all on page"
                    data-testid="invoice-select-all"
                  />
                </th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Invoice #</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Customer</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Client Ref</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Jobs</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Invoice Date</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Due Date</th>
                <th className="px-3 py-3 text-right whitespace-nowrap">Amount</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-left whitespace-nowrap">RCTI</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={TOTAL_COLS} className="px-3 py-6 text-center text-white/40">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={TOTAL_COLS} className="px-3 py-6 text-center text-white/40">
                  {searching ? "No invoices match your search." : tab === "merged" ? "No merged invoices yet — select invoices in the Normal tab and merge them." : "No invoices yet."}
                </td></tr>
              )}
              {pageItems.map((inv) => {
                const jobGroups = inv.type === "merged" ? groupLinesByJob(inv) : [];
                const isOpen = !!expanded[inv.id];
                return (
                  <React.Fragment key={inv.id}>
                    <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" checked={!!selected[inv.id]} onChange={(e) => setSelected((s) => ({ ...s, [inv.id]: e.target.checked }))}
                          className="accent-pink-500 w-4 h-4 cursor-pointer" aria-label={`Select ${inv.invoice_number}`} data-testid={`invoice-sel-${inv.id}`} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-white font-semibold font-mono flex items-center gap-2">
                          {inv.invoice_number}
                          {inv.edited && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300">edited</span>}
                        </div>
                        {(inv.job_refs || []).length > 0 && (
                          <div className="text-[10px] font-mono text-white/40 mt-0.5 truncate max-w-[160px]" title={(inv.job_refs || []).join(", ")}>
                            {(inv.job_refs || []).join(", ")}
                          </div>
                        )}
                        {inv.type === "merged" && (
                          <div className="text-[10px] text-pink-300 mt-0.5">consolidates {inv.child_numbers?.length || inv.child_invoice_ids?.length || 0} invoice(s)</div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-white/85 max-w-[180px] truncate" title={inv.customer_name}>{inv.customer_name}</td>
                      <td className="px-3 py-3 text-white/85 max-w-[160px] truncate" title={(inv.client_job_refs || []).join(", ")}>
                        {(inv.client_job_refs || []).join(", ") || "—"}
                      </td>
                      {inv.type === "merged" ? (
                        <td className="px-3 py-3">
                          <button onClick={() => setExpanded((x) => ({ ...x, [inv.id]: !x[inv.id] }))}
                            className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white"
                            data-testid={`merged-expand-${inv.id}`} title="Show the individual jobs merged into this invoice">
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="font-mono">{(inv.job_refs || []).length} job{(inv.job_refs || []).length !== 1 ? "s" : ""}</span>
                          </button>
                        </td>
                      ) : (
                        <td className="px-3 py-3 text-white/30">—</td>
                      )}
                      <td className="px-3 py-3 text-white/70 whitespace-nowrap text-xs font-mono">{inv.invoice_date ? formatDate(inv.invoice_date) : "—"}</td>
                      <td className="px-3 py-3 text-white/70 whitespace-nowrap text-xs font-mono">{inv.due_date ? formatDate(inv.due_date) : "—"}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-white whitespace-nowrap">{fmt(inv.total)}</td>
                      <td className="px-3 py-3">
                        <StatusDropdown options={INVOICE_STATUS_OPTIONS} value={inv.invoice_status} onChange={(v) => setStatus(inv, "invoice_status", v)} disabled={busyId === inv.id} testId={`inv-status-${inv.id}`} />
                      </td>
                      <td className="px-3 py-3">
                        <StatusDropdown options={RCTI_STATUS_OPTIONS} value={inv.rcti_status} onChange={(v) => setStatus(inv, "rcti_status", v)} disabled={busyId === inv.id} testId={`rcti-status-${inv.id}`} />
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setSending(inv)}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 inline-flex items-center gap-1"
                            title="Send this invoice to the client by email (Resend)" data-testid={`send-${inv.id}`}>
                            <Send size={13} /> Send
                          </button>
                          <button onClick={() => editInvoice(inv)} className="text-white/40 hover:text-[#19e6c8] p-1.5" title="Edit line items"><Pencil size={15} /></button>
                          <button onClick={() => openPdf(inv)} className="text-white/40 hover:text-white p-1.5" title="Open invoice PDF"><FileDown size={15} /></button>
                          <button onClick={() => downloadPdf(inv)} className="text-white/40 hover:text-white p-1.5" title="Download invoice PDF" data-testid={`download-${inv.id}`}><Download size={15} /></button>
                          {inv.type === "merged" && (
                            <button onClick={() => doUnmerge(inv)} className="text-white/40 hover:text-amber-400 p-1.5" title="Unmerge"><Split size={15} /></button>
                          )}
                          <button onClick={() => doDelete(inv)} className="text-white/40 hover:text-red-400 p-1.5" title="Delete invoice" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                    {inv.type === "merged" && isOpen && (
                      <tr className="bg-white/[0.015]" data-testid={`merged-detail-${inv.id}`}>
                        <td colSpan={TOTAL_COLS} className="px-6 py-3">
                          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                            Individual jobs in this merged invoice
                            {inv.child_numbers?.length ? ` · originally ${inv.child_numbers.join(", ")}` : ""}
                          </div>
                          <div className="space-y-2">
                            {jobGroups.map((g) => (
                              <div key={g.job_id || g.job_ref} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-pink-300">{g.job_ref}</span>
                                    {inv.client_ref_by_job?.[g.job_id] && (
                                      <span className="text-[10px] text-white/40">Client Ref: <span className="text-white/65 font-mono">{inv.client_ref_by_job[g.job_id]}</span></span>
                                    )}
                                  </div>
                                  <span className="font-mono text-xs text-white/70">{fmt(g.subtotal)} <span className="text-white/35">ex GST</span></span>
                                </div>
                                <table className="w-full text-[11px]">
                                  <tbody>
                                    {g.lines.map((l, i) => (
                                      <tr key={l.id || i}>
                                        <td className="py-0.5 text-white/70">{l.description}</td>
                                        <td className="py-0.5 text-right font-mono text-white/55 whitespace-nowrap">{Number(l.qty || 0)} × {fmt(l.rate)}</td>
                                        <td className="py-0.5 text-right font-mono text-white/80 whitespace-nowrap pl-3">{fmt(Number(l.qty || 0) * Number(l.rate || 0))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                            {jobGroups.length === 0 && <div className="text-white/40 text-xs">No line items.</div>}
                          </div>
                          <div className="mt-2 flex justify-end gap-4 text-xs">
                            <button onClick={() => editInvoice(inv)} className="text-[#19e6c8] hover:underline">Edit components</button>
                            <button onClick={() => setSending(inv)} className="text-emerald-300 hover:underline">Send to client</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePager page={page} totalPages={totalPages} total={filtered.length} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      {sending && (
        <SendInvoiceModal invoice={sending} onClose={() => setSending(null)} onSent={() => { setSending(null); load(); }} />
      )}

      {confirmDeleteOpen && (
        <BulkDeleteConfirmModal
          count={selectedList.length}
          busy={bulkBusy}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={bulkDeleteSelected}
        />
      )}
    </div>
  );
}

function BulkDeleteConfirmModal({ count, busy, onCancel, onConfirm }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0b1430] border border-white/10 rounded-2xl w-full max-w-md my-auto" data-testid="bulk-delete-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="brand-title text-xl flex items-center gap-2">
            <Trash2 size={17} className="text-red-400" /> Delete {count} invoice{count !== 1 ? "s" : ""}?
          </h3>
          <button type="button" onClick={onCancel} className="text-white/60 hover:text-white"><X size={20} /></button>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-white/70">
            This permanently deletes the {count} selected invoice{count !== 1 ? "s" : ""}. Deleting a merged invoice
            restores its original invoices rather than deleting them. This cannot be undone.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white/80 text-sm font-semibold">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-5 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2 disabled:opacity-50"
            style={{ background: "linear-gradient(90deg,#ef4444,#dc2626)" }}
            data-testid="confirm-bulk-delete-btn"
          >
            <Trash2 size={15} /> {busy ? "Deleting…" : `Delete (${count})`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
